export type TermCategory = 
  | "impuestos"
  | "regimenes"
  | "facturacion"
  | "declaraciones"
  | "deducciones"
  | "sat"
  | "contables"
  | "situaciones";

export interface GlosarioTerm {
  slug: string;
  name: string;
  shortDefinition: string;
  category: TermCategory;
  categoryLabel: string;
  priority: "alta" | "media" | "basica";
  keywords: string[];
  internalLink?: string;
  content: {
    whatIs: string;
    forWhat?: string;
    whoIsObliged?: string;
    howItWorks?: string;
    practicalExample?: string;
    commonMistakes?: string[];
    tips?: string[];
    faqs: { question: string; answer: string }[];
  };
  relatedTerms: string[];
  ctaText: string;
  ctaLink: string;
}

export const categories: { id: TermCategory; label: string; icon: string }[] = [
  { id: "impuestos", label: "Impuestos", icon: "💰" },
  { id: "regimenes", label: "Regímenes fiscales", icon: "📋" },
  { id: "facturacion", label: "Facturación", icon: "🧾" },
  { id: "declaraciones", label: "Declaraciones", icon: "📅" },
  { id: "deducciones", label: "Deducciones", icon: "✂️" },
  { id: "sat", label: "Trámites SAT", icon: "🏛️" },
  { id: "contables", label: "Términos contables", icon: "📊" },
  { id: "situaciones", label: "Situaciones especiales", icon: "⚠️" },
];

export const glosarioTerms: GlosarioTerm[] = [
  // IMPUESTOS - PRIORIDAD ALTA
  {
    slug: "isr",
    name: "ISR (Impuesto Sobre la Renta)",
    shortDefinition: "El impuesto que pagas sobre tus ganancias. Entre más ganas, mayor porcentaje pagas.",
    category: "impuestos",
    categoryLabel: "Impuesto",
    priority: "alta",
    keywords: ["qué es el isr", "impuesto sobre la renta méxico", "cómo calcular isr", "isr sat"],
    content: {
      whatIs: "El Impuesto Sobre la Renta es el impuesto más importante de México. Grava tus ingresos: lo que ganas por trabajar, por tu negocio, por rentar propiedades, o por inversiones.\n\nA diferencia del IVA (que se cobra sobre ventas), el ISR se cobra sobre tu UTILIDAD: lo que te queda después de restar tus gastos. Por eso las deducciones son tan importantes.",
      howItWorks: "Depende de cuánto ganes. Las tasas van del 1.92% hasta el 35%:\n\n• Si ganas poco, pagas poco (tasa baja)\n• Si ganas mucho, pagas más (tasa alta)\n\nEsto se llama 'tarifa progresiva' y es la razón por la que no hay un porcentaje único.",
      practicalExample: "María es freelancer y ganó $50,000 este mes. Sus gastos deducibles fueron $15,000.\n\n• Ingresos: $50,000\n• Deducciones: $15,000\n• Base gravable: $35,000\n• ISR aproximado: ~$4,000 (dependiendo de acumulado del año)",
      tips: [
        "Se paga mensualmente (pagos provisionales)",
        "Se ajusta en la declaración anual",
        "Puedes tener saldo a favor si pagaste de más"
      ],
      faqs: [
        { question: "¿Todos pagan ISR?", answer: "Todos los que tienen ingresos. Pero hay algunas excepciones y exenciones dependiendo del tipo de ingreso y el monto." },
        { question: "¿Qué pasa si no pago el ISR?", answer: "Multas, recargos y en casos graves, problemas legales con el SAT." },
        { question: "¿Cuándo se paga el ISR?", answer: "Mensualmente a través de pagos provisionales (antes del día 17) y se ajusta en la declaración anual en abril." }
      ]
    },
    relatedTerms: ["iva", "pago-provisional", "declaracion-anual", "deduccion-fiscal", "base-gravable"],
    ctaText: "¿Te preocupa pagar de más? Revisamos tu situación gratis",
    ctaLink: "/registro"
  },
  {
    slug: "iva",
    name: "IVA (Impuesto al Valor Agregado)",
    shortDefinition: "El impuesto del 16% que se cobra en la venta de productos y servicios.",
    category: "impuestos",
    categoryLabel: "Impuesto",
    priority: "alta",
    keywords: ["qué es el iva", "iva 16%", "iva en méxico", "cómo funciona el iva"],
    content: {
      whatIs: "El Impuesto al Valor Agregado es un impuesto indirecto que se cobra sobre el consumo. Se aplica en cada etapa de la cadena productiva, desde la fabricación hasta la venta final.\n\nLa tasa general es del 16%, aunque existen productos con tasa 0% (como alimentos básicos y medicinas) y algunos servicios exentos.",
      howItWorks: "Como contribuyente, cobras IVA a tus clientes y pagas IVA a tus proveedores. Al final del mes, restas el IVA que pagaste del IVA que cobraste:\n\n• Si cobraste más del que pagaste → Pagas la diferencia al SAT\n• Si pagaste más del que cobraste → Tienes saldo a favor",
      practicalExample: "Vendes un servicio por $10,000 + IVA:\n\n• Cobras a tu cliente: $10,000 + $1,600 (IVA) = $11,600\n• Tus gastos del mes con IVA: $5,000 + $800 (IVA)\n• IVA a pagar: $1,600 - $800 = $800",
      tips: [
        "El IVA no es tuyo, solo lo 'trasladas' al SAT",
        "Guarda todas tus facturas de gastos para acreditar el IVA",
        "Se declara y paga mensualmente"
      ],
      faqs: [
        { question: "¿Qué productos tienen IVA 0%?", answer: "Alimentos no procesados, medicinas de patente, libros y periódicos, entre otros." },
        { question: "¿Quién paga realmente el IVA?", answer: "El consumidor final. Los negocios solo lo trasladan." },
        { question: "¿Qué es el IVA acreditable?", answer: "El IVA que pagaste en tus compras y que puedes restar del IVA que cobraste." }
      ]
    },
    relatedTerms: ["isr", "acreditamiento-iva", "cfdi", "declaracion-mensual"],
    ctaText: "Nosotros declaramos tu IVA por ti",
    ctaLink: "/registro"
  },
  {
    slug: "ieps",
    name: "IEPS (Impuesto Especial sobre Producción y Servicios)",
    shortDefinition: "Impuesto adicional a productos como gasolina, alcohol, tabaco y bebidas azucaradas.",
    category: "impuestos",
    categoryLabel: "Impuesto",
    priority: "media",
    keywords: ["qué es ieps", "impuesto especial", "ieps gasolina"],
    content: {
      whatIs: "El IEPS es un impuesto especial que se aplica a productos considerados de lujo o que pueden afectar la salud o el medio ambiente. Se cobra además del IVA.",
      forWhat: "Sirve para desincentivar el consumo de ciertos productos y generar ingresos adicionales para el gobierno.",
      howItWorks: "Las tasas varían según el producto:\n\n• Gasolinas y diésel: cuota fija por litro\n• Bebidas alcohólicas: 26.5% a 53%\n• Tabaco: 160%\n• Bebidas azucaradas: $1.2616 por litro",
      faqs: [
        { question: "¿Quién paga el IEPS?", answer: "Los productores, importadores y comercializadores de estos productos, pero el costo se traslada al consumidor final." },
        { question: "¿Puedo deducir el IEPS?", answer: "Depende del producto y tu régimen fiscal. Generalmente no es acreditable como el IVA." }
      ]
    },
    relatedTerms: ["iva", "isr"],
    ctaText: "¿Vendes productos con IEPS? Te ayudamos",
    ctaLink: "/empresas"
  },
  {
    slug: "retencion-de-impuestos",
    name: "Retención de impuestos",
    shortDefinition: "Cuando alguien te descuenta impuestos antes de pagarte, para entregárselos al SAT.",
    category: "impuestos",
    categoryLabel: "Impuesto",
    priority: "media",
    keywords: ["retención isr", "retención iva", "qué es retención de impuestos"],
    content: {
      whatIs: "La retención de impuestos es cuando una persona moral (empresa) te paga por un servicio pero te descuenta una parte para pagársela directamente al SAT a tu nombre.",
      howItWorks: "Las retenciones más comunes son:\n\n• ISR: 10% del monto del servicio\n• IVA: 2/3 partes (10.6667%) del IVA\n\nEstas retenciones las puedes acreditar en tus declaraciones.",
      practicalExample: "Facturas $10,000 + IVA a una empresa:\n\n• Subtotal: $10,000\n• IVA (16%): $1,600\n• Retención ISR (10%): -$1,000\n• Retención IVA (2/3): -$1,066.67\n• Total a recibir: $9,533.33",
      tips: [
        "Guarda tus constancias de retención",
        "Las retenciones cuentan como impuesto ya pagado",
        "Puedes generar saldo a favor con muchas retenciones"
      ],
      faqs: [
        { question: "¿Quién está obligado a retener?", answer: "Las personas morales (empresas) cuando pagan a personas físicas por servicios profesionales." },
        { question: "¿Cómo recupero mis retenciones?", answer: "Las acreditas en tu declaración mensual y anual. Si pagaste de más, puedes solicitar devolución." }
      ]
    },
    relatedTerms: ["isr", "iva", "persona-fisica", "persona-moral"],
    ctaText: "¿Tienes muchas retenciones? Podemos ayudarte",
    ctaLink: "/registro"
  },

  // REGÍMENES FISCALES
  {
    slug: "resico",
    name: "RESICO (Régimen Simplificado de Confianza)",
    shortDefinition: "El régimen fiscal más sencillo de México. Pagas entre 1% y 2.5% de impuestos sobre lo que cobras.",
    category: "regimenes",
    categoryLabel: "Régimen fiscal",
    priority: "alta",
    keywords: ["qué es resico", "resico sat", "requisitos resico", "resico 2025"],
    internalLink: "/resico",
    content: {
      whatIs: "El Régimen Simplificado de Confianza es el régimen más sencillo y económico para personas físicas en México. Fue creado para simplificar el pago de impuestos a pequeños contribuyentes.",
      whoIsObliged: "Personas físicas con ingresos anuales de hasta $3,500,000 pesos que realizan actividades empresariales, profesionales o de arrendamiento.",
      howItWorks: "Las tasas van del 1% al 2.5% según tus ingresos:\n\n• Hasta $25,000 mensuales: 1%\n• De $25,000 a $50,000: 1.10%\n• De $50,000 a $83,333: 1.50%\n• De $83,333 a $208,333: 2%\n• De $208,333 a $291,666: 2.50%",
      practicalExample: "Si ganas $30,000 al mes:\n\n• Tasa aplicable: 1.10%\n• ISR a pagar: $330\n• Así de simple",
      commonMistakes: [
        "No estar al corriente con obligaciones anteriores",
        "Exceder el límite de ingresos sin avisar",
        "No emitir facturas por todos los ingresos"
      ],
      tips: [
        "Es el régimen ideal para emprendedores y freelancers",
        "No necesitas contador para cosas básicas",
        "Tus declaraciones se pueden autocalcular en el SAT"
      ],
      faqs: [
        { question: "¿Puedo estar en RESICO si también soy asalariado?", answer: "Sí, puedes combinar RESICO con ingresos por sueldos y salarios." },
        { question: "¿Qué pasa si supero los $3.5 millones?", answer: "Debes cambiar a Actividad Empresarial y Profesional desde el siguiente año." },
        { question: "¿Puedo deducir gastos en RESICO?", answer: "No, en RESICO no hay deducciones. El impuesto se calcula directo sobre lo que cobras." }
      ]
    },
    relatedTerms: ["actividad-empresarial", "persona-fisica", "isr", "declaracion-mensual"],
    ctaText: "¿No sabes si te conviene RESICO? Te ayudamos a elegir",
    ctaLink: "/resico"
  },
  {
    slug: "actividad-empresarial",
    name: "Actividad Empresarial y Profesional",
    shortDefinition: "El régimen para negocios y profesionistas sin límite de ingresos, donde puedes deducir todos tus gastos.",
    category: "regimenes",
    categoryLabel: "Régimen fiscal",
    priority: "media",
    keywords: ["actividad empresarial", "régimen actividad empresarial", "profesional independiente"],
    internalLink: "/actividad-empresarial",
    content: {
      whatIs: "Es el régimen fiscal para personas físicas que realizan actividades comerciales, industriales o prestan servicios profesionales de manera independiente. No tiene límite de ingresos.",
      howItWorks: "A diferencia de RESICO, aquí sí puedes deducir todos tus gastos relacionados con tu actividad:\n\n• Ingresos - Gastos deducibles = Utilidad\n• Sobre esa utilidad pagas ISR (tasas del 1.92% al 35%)",
      practicalExample: "Ingresos del mes: $50,000\nGastos deducibles: $20,000\nUtilidad: $30,000\nISR aproximado: ~$3,500",
      tips: [
        "Ideal si tienes muchos gastos deducibles",
        "Puedes emitir facturas sin límite",
        "Requiere llevar contabilidad más formal"
      ],
      faqs: [
        { question: "¿Cuándo me conviene este régimen?", answer: "Cuando tus gastos deducibles representan más del 40-50% de tus ingresos." },
        { question: "¿Qué puedo deducir?", answer: "Gastos estrictamente indispensables para tu actividad: renta, luz, internet, equipo, etc." }
      ]
    },
    relatedTerms: ["resico", "deduccion-fiscal", "isr", "persona-fisica"],
    ctaText: "¿No sabes en qué régimen estar? Te ayudamos a elegir",
    ctaLink: "/actividad-empresarial"
  },
  {
    slug: "regimen-arrendamiento",
    name: "Régimen de Arrendamiento",
    shortDefinition: "El régimen fiscal para personas que rentan casas, departamentos, locales o terrenos.",
    category: "regimenes",
    categoryLabel: "Régimen fiscal",
    priority: "media",
    keywords: ["régimen arrendamiento", "impuestos por rentar", "arrendamiento fiscal"],
    internalLink: "/arrendamiento",
    content: {
      whatIs: "Es el régimen fiscal obligatorio para personas físicas que obtienen ingresos por rentar bienes inmuebles: casas, departamentos, locales comerciales o terrenos.",
      howItWorks: "Tienes dos opciones para calcular tus impuestos:\n\n1. Deducción ciega: Deducir automáticamente 35% de tus rentas (sin comprobantes)\n2. Deducciones reales: Deducir gastos comprobados (predial, mantenimiento, etc.)",
      practicalExample: "Rentas un departamento por $15,000 mensuales:\n\nOpción 1 - Deducción ciega:\n• Ingreso: $15,000\n• Deducción (35%): $5,250\n• Base gravable: $9,750\n\nOpción 2 - Gastos reales:\n• Depende de tus gastos comprobados",
      tips: [
        "La deducción ciega es más simple",
        "Debes emitir facturas a tus inquilinos",
        "Puedes combinar con otros regímenes"
      ],
      faqs: [
        { question: "¿Qué conviene más: deducción ciega o real?", answer: "Si tus gastos son menores al 35%, la ciega. Si son mayores, las reales." },
        { question: "¿Debo facturar a mi inquilino?", answer: "Sí, estás obligado a emitir CFDI por cada renta." }
      ]
    },
    relatedTerms: ["deduccion-ciega", "isr", "cfdi", "persona-fisica"],
    ctaText: "¿Rentas propiedades? Nosotros declaramos por ti",
    ctaLink: "/arrendamiento"
  },
  {
    slug: "plataformas-tecnologicas",
    name: "Plataformas Tecnológicas",
    shortDefinition: "El régimen para quienes generan ingresos a través de apps como Uber, Rappi, Airbnb o Mercado Libre.",
    category: "regimenes",
    categoryLabel: "Régimen fiscal",
    priority: "media",
    keywords: ["uber impuestos", "rappi impuestos", "plataformas digitales sat"],
    internalLink: "/plataformas-tecnologicas",
    content: {
      whatIs: "Es el régimen para personas que obtienen ingresos mediante plataformas digitales como Uber, Didi, Rappi, Airbnb, Mercado Libre, etc.",
      howItWorks: "Las plataformas retienen y pagan tus impuestos automáticamente:\n\n• Retienen ISR (hasta 8% según actividad)\n• Retienen IVA (8%)\n\nEsta retención puede ser tu pago definitivo o solo un anticipo.",
      practicalExample: "Ganaste $10,000 en Uber este mes:\n\n• Uber retiene ~$800 de ISR\n• Uber retiene ~$800 de IVA\n• Tú recibes ~$8,400",
      tips: [
        "Puedes optar por que las retenciones sean pago definitivo",
        "Debes darte de alta en el SAT con este régimen",
        "Considera si te conviene más RESICO"
      ],
      faqs: [
        { question: "¿La plataforma paga mis impuestos?", answer: "Sí, las retiene de tus ganancias y las paga al SAT." },
        { question: "¿Necesito hacer declaraciones?", answer: "Si optas por retención definitiva, solo la anual. Si no, mensuales también." }
      ]
    },
    relatedTerms: ["resico", "isr", "iva", "retencion-de-impuestos"],
    ctaText: "¿Ganas en apps? Te ayudamos con tus impuestos",
    ctaLink: "/plataformas-tecnologicas"
  },
  {
    slug: "sueldos-salarios",
    name: "Sueldos y Salarios",
    shortDefinition: "El régimen para empleados. Tu patrón te retiene y paga tus impuestos.",
    category: "regimenes",
    categoryLabel: "Régimen fiscal",
    priority: "basica",
    keywords: ["régimen asalariados", "impuestos empleados"],
    content: {
      whatIs: "Es el régimen fiscal para personas que trabajan como empleados formales. Tu patrón se encarga de calcular, retener y pagar tus impuestos al SAT.",
      howItWorks: "Tu patrón calcula tu ISR según tablas del SAT y te lo descuenta de tu nómina cada quincena o mes. Al final del año, si aplica, debes presentar declaración anual.",
      faqs: [
        { question: "¿Debo hacer algo con el SAT?", answer: "Generalmente no, tu patrón hace todo. Solo la declaración anual si ganas más de $400,000 al año." },
        { question: "¿Puedo deducir gastos?", answer: "Solo deducciones personales en la declaración anual: gastos médicos, colegiaturas, etc." }
      ]
    },
    relatedTerms: ["isr", "declaracion-anual", "deducciones-personales"],
    ctaText: "¿Tienes trabajo e ingresos extra? Te ayudamos",
    ctaLink: "/registro"
  },
  {
    slug: "persona-fisica",
    name: "Persona Física",
    shortDefinition: "Cualquier ser humano que realiza actividades económicas. Tú, yo, tu vecino.",
    category: "regimenes",
    categoryLabel: "Régimen fiscal",
    priority: "alta",
    keywords: ["persona física", "qué es persona física fiscal"],
    content: {
      whatIs: "Una persona física eres tú: un ser humano individual que puede tener derechos y obligaciones fiscales. Cualquier persona que trabaja, vende o presta servicios es una persona física contribuyente.",
      howItWorks: "Como persona física puedes estar en diferentes regímenes según tu actividad:\n\n• RESICO: emprendedores y freelancers\n• Actividad Empresarial: negocios sin límite\n• Arrendamiento: si rentas propiedades\n• Sueldos: si eres empleado",
      faqs: [
        { question: "¿Cuál es la diferencia con persona moral?", answer: "Persona física = ser humano. Persona moral = empresa o sociedad." },
        { question: "¿Puedo tener varios regímenes?", answer: "Sí, puedes combinar regímenes según tus actividades." }
      ]
    },
    relatedTerms: ["persona-moral", "resico", "actividad-empresarial", "regimen-fiscal"],
    ctaText: "¿No sabes en qué régimen estar?",
    ctaLink: "/registro"
  },
  {
    slug: "persona-moral",
    name: "Persona Moral",
    shortDefinition: "Una empresa o sociedad legalmente constituida. Existe en papel, no en carne y hueso.",
    category: "regimenes",
    categoryLabel: "Régimen fiscal",
    priority: "alta",
    keywords: ["persona moral", "qué es persona moral", "empresa fiscal"],
    content: {
      whatIs: "Una persona moral es una entidad jurídica formada por una o más personas físicas. Son las empresas, asociaciones civiles, sociedades, etc. Tienen RFC propio y obligaciones fiscales independientes.",
      howItWorks: "Las personas morales pagan ISR sobre sus utilidades (generalmente 30%) y tienen obligaciones contables más complejas que las personas físicas.",
      faqs: [
        { question: "¿Cuándo me conviene crear una persona moral?", answer: "Generalmente cuando facturas más de $3-4 millones al año o necesitas separar tu patrimonio personal." },
        { question: "¿Qué tipos de personas morales hay?", answer: "SA, S de RL, SC, AC, entre otras. Cada una tiene características diferentes." }
      ]
    },
    relatedTerms: ["persona-fisica", "isr"],
    ctaText: "¿Necesitas constituir una empresa?",
    ctaLink: "/empresas"
  },
  {
    slug: "regimen-fiscal",
    name: "Régimen Fiscal",
    shortDefinition: "La categoría en la que el SAT te clasifica según tu actividad económica.",
    category: "regimenes",
    categoryLabel: "Régimen fiscal",
    priority: "basica",
    keywords: ["régimen fiscal", "tipos de régimen fiscal"],
    content: {
      whatIs: "El régimen fiscal es la clasificación que el SAT te asigna según el tipo de actividad económica que realizas. Determina qué impuestos pagas, cómo los calculas y qué obligaciones tienes.",
      howItWorks: "Los principales regímenes para personas físicas son:\n\n• RESICO: el más simple\n• Actividad Empresarial: para negocios\n• Arrendamiento: para rentas\n• Sueldos y Salarios: para empleados\n• Plataformas Tecnológicas: para apps",
      faqs: [
        { question: "¿Cómo sé en qué régimen estoy?", answer: "Revisa tu Constancia de Situación Fiscal en el portal del SAT." },
        { question: "¿Puedo cambiar de régimen?", answer: "Sí, presentando un aviso de actualización en el SAT." }
      ]
    },
    relatedTerms: ["resico", "actividad-empresarial", "constancia-situacion-fiscal"],
    ctaText: "¿No sabes en qué régimen estar?",
    ctaLink: "/registro"
  },

  // FACTURACIÓN Y COMPROBANTES
  {
    slug: "cfdi",
    name: "CFDI (Comprobante Fiscal Digital por Internet)",
    shortDefinition: "La factura electrónica oficial de México. El único comprobante fiscal válido.",
    category: "facturacion",
    categoryLabel: "Facturación",
    priority: "alta",
    keywords: ["qué es cfdi", "factura electrónica", "cfdi 4.0", "cfdi sat"],
    content: {
      whatIs: "El CFDI es la versión digital y legalmente válida de una factura en México. Desde 2014, es obligatorio para todos los contribuyentes. La versión actual es CFDI 4.0.",
      howItWorks: "Un CFDI válido debe:\n\n• Ser emitido por un PAC (Proveedor Autorizado de Certificación)\n• Tener un sello digital del SAT\n• Contener todos los datos fiscales requeridos\n• Tener un UUID único",
      commonMistakes: [
        "No verificar el RFC del receptor",
        "Usar el uso de CFDI incorrecto",
        "No incluir el régimen fiscal correcto"
      ],
      tips: [
        "Siempre verifica que el RFC sea correcto",
        "Emite tus facturas antes del fin de mes",
        "Guarda todas tus facturas emitidas y recibidas"
      ],
      faqs: [
        { question: "¿Es lo mismo CFDI que factura?", answer: "Sí, CFDI es el nombre técnico de la factura electrónica." },
        { question: "¿Qué necesito para emitir CFDI?", answer: "Tu e.firma, tu CSD (Certificado de Sello Digital) y un sistema de facturación." },
        { question: "¿Cuánto tiempo debo guardar mis CFDI?", answer: "5 años a partir de que se presenta la declaración donde se utilizan." }
      ]
    },
    relatedTerms: ["factura-electronica", "uuid", "efirma", "csd"],
    ctaText: "Factura ilimitado desde $295/mes",
    ctaLink: "/registro"
  },
  {
    slug: "factura-electronica",
    name: "Factura Electrónica",
    shortDefinition: "Es lo mismo que un CFDI. El documento digital que comprueba una venta o servicio.",
    category: "facturacion",
    categoryLabel: "Facturación",
    priority: "alta",
    keywords: ["factura electrónica", "cómo hacer factura", "facturación sat"],
    content: {
      whatIs: "La factura electrónica (CFDI) es el comprobante fiscal digital obligatorio en México. Registra las transacciones comerciales y es indispensable para deducir gastos.",
      forWhat: "Sirve para:\n\n• Comprobar ingresos y gastos\n• Deducir impuestos\n• Llevar control de operaciones\n• Cumplir obligaciones fiscales",
      faqs: [
        { question: "¿Debo facturar todo lo que vendo?", answer: "Sí, todos tus ingresos deben estar respaldados por un CFDI." },
        { question: "¿Puedo facturar desde mi celular?", answer: "Sí, hay apps y servicios que te permiten facturar desde cualquier dispositivo." }
      ]
    },
    relatedTerms: ["cfdi", "uuid", "efirma"],
    ctaText: "Factura desde tu celular con SinConta",
    ctaLink: "/registro"
  },
  {
    slug: "rfc",
    name: "RFC (Registro Federal de Contribuyentes)",
    shortDefinition: "Tu identificación fiscal única de 13 caracteres. Como tu CURP, pero para impuestos.",
    category: "facturacion",
    categoryLabel: "Identificación fiscal",
    priority: "alta",
    keywords: ["qué es rfc", "cómo sacar rfc", "rfc sat", "registro federal de contribuyentes"],
    content: {
      whatIs: "El RFC es tu clave única ante el SAT. Son 13 caracteres para personas físicas (10 para morales) que te identifican como contribuyente.",
      howItWorks: "El RFC se forma con:\n\n• Primeras letras de tu nombre y apellidos\n• Tu fecha de nacimiento\n• Una homoclave asignada por el SAT\n\nEjemplo: GAPA850101ABC",
      faqs: [
        { question: "¿Cómo obtengo mi RFC?", answer: "Dándote de alta en el SAT presencialmente o por internet si tienes e.firma." },
        { question: "¿RFC genérico qué es?", answer: "Es XAXX010101000, usado cuando el cliente no proporciona su RFC." },
        { question: "¿Puedo tener varios RFC?", answer: "No, cada persona solo puede tener un RFC." }
      ]
    },
    relatedTerms: ["sat", "efirma", "constancia-situacion-fiscal"],
    ctaText: "¿Necesitas darte de alta en el SAT?",
    ctaLink: "/registro"
  },
  {
    slug: "efirma",
    name: "e.firma (Firma Electrónica)",
    shortDefinition: "Tu firma digital para trámites del SAT. Es como tu autógrafo, pero en formato electrónico.",
    category: "facturacion",
    categoryLabel: "Identificación digital",
    priority: "alta",
    keywords: ["qué es efirma", "firma electrónica sat", "cómo sacar efirma"],
    content: {
      whatIs: "La e.firma (antes FIEL) es tu firma electrónica oficial ante el SAT. Son dos archivos (.cer y .key) más una contraseña que te identifican de manera única.",
      forWhat: "Con tu e.firma puedes:\n\n• Presentar declaraciones\n• Hacer trámites en línea\n• Firmar documentos electrónicos\n• Obtener tu CSD para facturar",
      howItWorks: "Tiene vigencia de 4 años y se obtiene presencialmente en el SAT. Necesitas tu INE, comprobante de domicilio y CURP.",
      faqs: [
        { question: "¿Es obligatoria la e.firma?", answer: "Sí, para la mayoría de contribuyentes activos." },
        { question: "¿Cómo la renuevo?", answer: "Antes de que venza, por internet. Si ya venció, presencialmente en el SAT." }
      ]
    },
    relatedTerms: ["csd", "rfc", "sat"],
    ctaText: "Te ayudamos con tus trámites SAT",
    ctaLink: "/contacto"
  },
  {
    slug: "csd",
    name: "CSD (Certificado de Sello Digital)",
    shortDefinition: "El archivo que necesitas para poder emitir facturas electrónicas.",
    category: "facturacion",
    categoryLabel: "Facturación",
    priority: "media",
    keywords: ["certificado de sello digital", "csd sat", "csd para facturar"],
    content: {
      whatIs: "El CSD es un certificado especial que te permite sellar (firmar) tus facturas electrónicas. Sin él, no puedes emitir CFDI.",
      howItWorks: "Se obtiene en el portal del SAT usando tu e.firma. Tiene vigencia de 4 años y son dos archivos (.cer y .key) diferentes a los de tu e.firma.",
      faqs: [
        { question: "¿Es lo mismo que la e.firma?", answer: "No. La e.firma es para trámites generales, el CSD es específico para facturación." },
        { question: "¿Puedo tener varios CSD?", answer: "Sí, puedes tener uno por cada sucursal o establecimiento." }
      ]
    },
    relatedTerms: ["cfdi", "efirma", "factura-electronica"],
    ctaText: "Te ayudamos a obtener tu CSD",
    ctaLink: "/registro"
  },
  {
    slug: "complemento-de-pago",
    name: "Complemento de Pago",
    shortDefinition: "El CFDI que emites cuando te pagan una factura que estaba a crédito (PPD).",
    category: "facturacion",
    categoryLabel: "Facturación",
    priority: "basica",
    keywords: ["complemento de pago sat", "recibo electrónico de pago"],
    content: {
      whatIs: "Es un CFDI especial que debes emitir cuando recibes el pago de una factura que se emitió como 'Pago en Parcialidades o Diferido' (PPD).",
      howItWorks: "Cuando emites una factura PPD (a crédito), al recibir el pago debes emitir un complemento de pago dentro de los siguientes 5 días hábiles.",
      faqs: [
        { question: "¿Cuándo debo emitirlo?", answer: "Máximo 5 días hábiles después de recibir el pago." },
        { question: "¿Qué pasa si no lo emito?", answer: "Multas del SAT y problemas para que tu cliente deduzca el gasto." }
      ]
    },
    relatedTerms: ["cfdi", "factura-electronica"],
    ctaText: "Te ayudamos con tu facturación",
    ctaLink: "/registro"
  },
  {
    slug: "nota-de-credito",
    name: "Nota de Crédito",
    shortDefinition: "La factura que cancela o reduce el monto de otra factura anterior.",
    category: "facturacion",
    categoryLabel: "Facturación",
    priority: "basica",
    keywords: ["nota de crédito sat", "cómo hacer nota de crédito"],
    content: {
      whatIs: "Es un CFDI de tipo 'Egreso' que se emite para anular parcial o totalmente el monto de una factura anterior. Se usa para devoluciones, descuentos o correcciones.",
      howItWorks: "Debe relacionarse con la factura original y especificar el monto a acreditar. Reduce tu ingreso gravable y el IVA correspondiente.",
      faqs: [
        { question: "¿Es lo mismo que cancelar una factura?", answer: "No. Cancelar elimina la factura. La nota de crédito reduce o ajusta el monto." },
        { question: "¿Cuándo usar nota de crédito?", answer: "Para devoluciones, descuentos posteriores, o bonificaciones." }
      ]
    },
    relatedTerms: ["cfdi", "cancelacion-facturas"],
    ctaText: "¿Necesitas ayuda con tu facturación?",
    ctaLink: "/registro"
  },
  {
    slug: "uuid",
    name: "UUID / Folio Fiscal",
    shortDefinition: "El código único de 36 caracteres que identifica cada factura electrónica.",
    category: "facturacion",
    categoryLabel: "Facturación",
    priority: "basica",
    keywords: ["uuid factura", "folio fiscal cfdi"],
    content: {
      whatIs: "El UUID (Universally Unique Identifier) o Folio Fiscal es el código único que identifica cada CFDI. Es asignado por el SAT y no puede repetirse.",
      howItWorks: "Tiene el formato: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx\n\nEjemplo: 6F8A0B2D-4E5F-1234-A678-9B0C1D2E3F4G",
      faqs: [
        { question: "¿Para qué sirve el UUID?", answer: "Para identificar de manera única cada factura y relacionar documentos." },
        { question: "¿Dónde lo encuentro?", answer: "En el XML y PDF de tu factura, generalmente en la parte superior." }
      ]
    },
    relatedTerms: ["cfdi", "factura-electronica"],
    ctaText: "Factura fácil con SinConta",
    ctaLink: "/registro"
  },

  // DECLARACIONES Y OBLIGACIONES
  {
    slug: "declaracion-anual",
    name: "Declaración Anual",
    shortDefinition: "El resumen de todos tus ingresos, gastos e impuestos del año. Se presenta en abril.",
    category: "declaraciones",
    categoryLabel: "Declaración",
    priority: "alta",
    keywords: ["declaración anual sat", "cuándo se presenta", "declaración anual personas físicas"],
    content: {
      whatIs: "La declaración anual es el cierre fiscal del año. En ella reportas todos tus ingresos, gastos y deducciones para calcular si debes impuestos adicionales o tienes saldo a favor.",
      whoIsObliged: "Personas físicas con:\n\n• Ingresos mayores a $400,000 anuales\n• Dos o más patrones en el año\n• Ingresos por honorarios, arrendamiento o actividad empresarial\n• Deducciones personales que quieran aplicar",
      howItWorks: "Se presenta en abril (del 1 al 30) del año siguiente. Ejemplo: la de 2024 se presenta en abril 2025.",
      faqs: [
        { question: "¿Qué pasa si no la presento?", answer: "Multas, recargos, y podrías quedar como 'no localizado' ante el SAT." },
        { question: "¿Puedo tener saldo a favor?", answer: "Sí, si pagaste más impuestos de los que debías. Puedes solicitar devolución." }
      ]
    },
    relatedTerms: ["isr", "deducciones-personales", "saldo-a-favor"],
    ctaText: "Nosotros presentamos tu declaración anual",
    ctaLink: "/registro"
  },
  {
    slug: "pago-provisional",
    name: "Pago Provisional",
    shortDefinition: "Los pagos mensuales de ISR que haces a cuenta de tu impuesto anual.",
    category: "declaraciones",
    categoryLabel: "Declaración",
    priority: "media",
    keywords: ["pago provisional isr", "declaración mensual sat"],
    content: {
      whatIs: "Los pagos provisionales son anticipos mensuales del ISR que debes al SAT. Se calculan con base en tus ingresos y se presentan antes del día 17 de cada mes.",
      howItWorks: "Se calcula multiplicando tus ingresos acumulados por un coeficiente de utilidad, y a ese resultado se le aplica la tasa de ISR correspondiente.",
      faqs: [
        { question: "¿Cuándo se presentan?", answer: "A más tardar el día 17 del mes siguiente al que corresponden." },
        { question: "¿Para qué sirven?", answer: "Para pagar el ISR gradualmente y no tener un monto muy alto en la declaración anual." }
      ]
    },
    relatedTerms: ["isr", "declaracion-anual", "coeficiente-utilidad"],
    ctaText: "Nosotros calculamos y presentamos tus pagos",
    ctaLink: "/registro"
  },
  {
    slug: "declaracion-mensual",
    name: "Declaración Mensual",
    shortDefinition: "Las declaraciones que presentas cada mes para reportar y pagar ISR e IVA.",
    category: "declaraciones",
    categoryLabel: "Declaración",
    priority: "basica",
    keywords: ["declaración mensual sat", "cómo hacer declaración mensual"],
    content: {
      whatIs: "Las declaraciones mensuales son los reportes periódicos donde informas al SAT tus ingresos, gastos e impuestos del mes. Incluyen ISR, IVA y, en algunos casos, otras obligaciones.",
      howItWorks: "Se presentan antes del día 17 de cada mes por el mes anterior. Ejemplo: la de enero se presenta a más tardar el 17 de febrero.",
      faqs: [
        { question: "¿Qué impuestos incluye?", answer: "Generalmente ISR e IVA, dependiendo de tu régimen." },
        { question: "¿Qué pasa si no las presento?", answer: "Multas por cada obligación omitida y actualizaciones/recargos sobre impuestos no pagados." }
      ]
    },
    relatedTerms: ["pago-provisional", "isr", "iva"],
    ctaText: "Nosotros presentamos tus declaraciones",
    ctaLink: "/registro"
  },
  {
    slug: "diot",
    name: "DIOT (Declaración Informativa de Operaciones con Terceros)",
    shortDefinition: "El reporte mensual donde informas al SAT con quién hiciste operaciones de IVA.",
    category: "declaraciones",
    categoryLabel: "Declaración",
    priority: "media",
    keywords: ["qué es diot", "diot sat", "cómo presentar diot"],
    content: {
      whatIs: "La DIOT es una declaración informativa donde reportas al SAT el IVA que pagaste a tus proveedores. No se paga impuesto, solo se informa.",
      whoIsObliged: "Contribuyentes que realizan operaciones con IVA y no están en RESICO (los de RESICO no la presentan).",
      howItWorks: "Se presenta mensualmente y debe incluir: RFC del proveedor, monto de operaciones, IVA pagado.",
      faqs: [
        { question: "¿Qué pasa si no la presento?", answer: "Multas y podrías no poder acreditar tu IVA pagado." },
        { question: "¿RESICO presenta DIOT?", answer: "No, los contribuyentes de RESICO están exentos de esta obligación." }
      ]
    },
    relatedTerms: ["iva", "acreditamiento-iva"],
    ctaText: "Nosotros presentamos tu DIOT",
    ctaLink: "/registro"
  },
  {
    slug: "contabilidad-electronica",
    name: "Contabilidad Electrónica",
    shortDefinition: "El envío de tu información contable al SAT en formato XML.",
    category: "declaraciones",
    categoryLabel: "Obligación",
    priority: "media",
    keywords: ["contabilidad electrónica sat", "catálogo de cuentas"],
    content: {
      whatIs: "Es la obligación de enviar al SAT tus registros contables en formato electrónico (XML): catálogo de cuentas, balanza de comprobación y pólizas.",
      whoIsObliged: "Personas físicas con ingresos mayores a 4 millones de pesos anuales y todas las personas morales.",
      howItWorks: "Se envía mensualmente la balanza de comprobación y se actualiza el catálogo de cuentas cuando hay cambios.",
      faqs: [
        { question: "¿RESICO envía contabilidad electrónica?", answer: "No, están exentos de esta obligación." },
        { question: "¿Qué información incluye?", answer: "Catálogo de cuentas, balanza de comprobación mensual, y pólizas (cuando el SAT las solicita)." }
      ]
    },
    relatedTerms: ["declaracion-mensual", "persona-moral"],
    ctaText: "Nosotros llevamos tu contabilidad",
    ctaLink: "/empresas"
  },

  // DEDUCCIONES Y BENEFICIOS
  {
    slug: "deduccion-fiscal",
    name: "Deducción Fiscal",
    shortDefinition: "Un gasto que puedes restar de tus ingresos para pagar menos impuestos.",
    category: "deducciones",
    categoryLabel: "Deducción",
    priority: "alta",
    keywords: ["qué es deducción fiscal", "gastos deducibles", "deducciones personales"],
    content: {
      whatIs: "Una deducción fiscal es un gasto que la ley te permite restar de tus ingresos antes de calcular tus impuestos. Entre más deducciones tengas, menos impuestos pagas.",
      howItWorks: "Hay dos tipos principales:\n\n1. Deducciones autorizadas: gastos de tu negocio\n2. Deducciones personales: gastos personales permitidos (médicos, colegiaturas, etc.)",
      faqs: [
        { question: "¿Qué requisitos debe cumplir un gasto deducible?", answer: "Tener factura a tu nombre, estar relacionado con tu actividad, y ser pagado de forma bancarizada." },
        { question: "¿Hay límites a las deducciones?", answer: "Sí, las deducciones personales tienen un límite anual." }
      ]
    },
    relatedTerms: ["deducciones-personales", "gastos-deducibles", "isr"],
    ctaText: "Descubre cuánto puedes deducir - Diagnóstico gratis",
    ctaLink: "/registro"
  },
  {
    slug: "deduccion-ciega",
    name: "Deducción Ciega",
    shortDefinition: "La opción de deducir 35% de tus ingresos por arrendamiento sin necesidad de comprobantes.",
    category: "deducciones",
    categoryLabel: "Deducción",
    priority: "media",
    keywords: ["deducción ciega arrendamiento", "35% deducción"],
    internalLink: "/arrendamiento",
    content: {
      whatIs: "Es una facilidad fiscal para quienes rentan inmuebles. Puedes deducir automáticamente el 35% de tus ingresos por renta sin necesidad de tener comprobantes de gastos.",
      howItWorks: "Si rentas por $10,000 mensuales:\n\n• Ingreso: $10,000\n• Deducción ciega (35%): $3,500\n• Base gravable: $6,500\n\nSobre $6,500 calculas tu ISR.",
      faqs: [
        { question: "¿Cuándo conviene usarla?", answer: "Cuando tus gastos reales (predial, mantenimiento, etc.) son menores al 35% de tu renta." },
        { question: "¿Puedo cambiar de opción?", answer: "Sí, cada año puedes elegir entre deducción ciega o gastos reales." }
      ]
    },
    relatedTerms: ["regimen-arrendamiento", "deduccion-fiscal", "isr"],
    ctaText: "¿Rentas propiedades? Te ayudamos",
    ctaLink: "/arrendamiento"
  },
  {
    slug: "deducciones-personales",
    name: "Deducciones Personales",
    shortDefinition: "Gastos de tu vida personal que puedes deducir en tu declaración anual: médicos, educación, etc.",
    category: "deducciones",
    categoryLabel: "Deducción",
    priority: "media",
    keywords: ["deducciones personales sat", "gastos deducibles personales"],
    content: {
      whatIs: "Son gastos de tu vida personal que la ley permite deducir en tu declaración anual para reducir el ISR que pagas o aumentar tu saldo a favor.",
      howItWorks: "Las principales deducciones personales son:\n\n• Gastos médicos y hospitalarios\n• Colegiaturas (con límites)\n• Intereses de hipoteca\n• Aportaciones a afore\n• Gastos funerarios\n• Primas de seguros de gastos médicos",
      faqs: [
        { question: "¿Hay un límite?", answer: "Sí, el menor entre 5 UMAs anuales (~$189,000) o el 15% de tus ingresos." },
        { question: "¿Cómo las aplico?", answer: "En tu declaración anual de abril." }
      ]
    },
    relatedTerms: ["declaracion-anual", "isr", "saldo-a-favor"],
    ctaText: "Te ayudamos a maximizar tus deducciones",
    ctaLink: "/registro"
  },
  {
    slug: "gastos-deducibles",
    name: "Gastos Deducibles",
    shortDefinition: "Los gastos de tu negocio que puedes restar para pagar menos ISR.",
    category: "deducciones",
    categoryLabel: "Deducción",
    priority: "basica",
    keywords: ["gastos deducibles negocio", "qué puedo deducir"],
    content: {
      whatIs: "Son los gastos que realizas para operar tu negocio y que puedes restar de tus ingresos antes de calcular impuestos.",
      howItWorks: "Para que un gasto sea deducible debe:\n\n• Ser estrictamente indispensable para tu actividad\n• Tener factura (CFDI) a tu nombre\n• Pagarse por medios electrónicos (transferencia, tarjeta)",
      faqs: [
        { question: "¿Qué gastos puedo deducir?", answer: "Renta del local, servicios (luz, internet), papelería, herramientas, sueldos, publicidad, etc." },
        { question: "¿Puedo deducir la comida?", answer: "Solo si es estrictamente necesaria para tu trabajo y con ciertos límites." }
      ]
    },
    relatedTerms: ["deduccion-fiscal", "isr", "cfdi"],
    ctaText: "¿No sabes qué puedes deducir? Te asesoramos",
    ctaLink: "/registro"
  },
  {
    slug: "acreditamiento-iva",
    name: "Acreditamiento de IVA",
    shortDefinition: "Restar el IVA que pagaste en tus compras del IVA que cobraste en tus ventas.",
    category: "deducciones",
    categoryLabel: "Beneficio fiscal",
    priority: "basica",
    keywords: ["acreditamiento iva", "iva acreditable"],
    content: {
      whatIs: "Es el derecho de restar el IVA que pagaste a tus proveedores del IVA que cobraste a tus clientes. La diferencia es lo que pagas (o te devuelven).",
      howItWorks: "IVA cobrado a clientes: $10,000\nIVA pagado a proveedores: $6,000\nIVA a pagar: $4,000\n\nSi pagas más del que cobras, tienes saldo a favor.",
      faqs: [
        { question: "¿Qué necesito para acreditar IVA?", answer: "Facturas (CFDI) válidas de tus compras a tu nombre." },
        { question: "¿Todo el IVA es acreditable?", answer: "Solo el de gastos relacionados con tu actividad gravada." }
      ]
    },
    relatedTerms: ["iva", "saldo-a-favor", "cfdi"],
    ctaText: "¿Tienes saldo a favor? Te ayudamos a recuperarlo",
    ctaLink: "/registro"
  },
  {
    slug: "saldo-a-favor",
    name: "Saldo a Favor",
    shortDefinition: "Cuando pagaste más impuestos de los que debías. El SAT te debe dinero.",
    category: "deducciones",
    categoryLabel: "Beneficio fiscal",
    priority: "media",
    keywords: ["saldo a favor sat", "devolución de impuestos"],
    content: {
      whatIs: "El saldo a favor ocurre cuando pagaste más impuestos de los que realmente debías. Puede ser por retenciones, pagos provisionales excesivos o deducciones.",
      howItWorks: "Tienes dos opciones:\n\n1. Solicitar devolución: El SAT te deposita el dinero\n2. Compensar: Usarlo para pagar impuestos futuros",
      faqs: [
        { question: "¿Cuánto tarda la devolución?", answer: "De 5 a 40 días hábiles, dependiendo del monto y si hay revisión." },
        { question: "¿Cómo lo solicito?", answer: "En tu declaración anual o mediante formato electrónico en el portal del SAT." }
      ]
    },
    relatedTerms: ["declaracion-anual", "isr", "iva"],
    ctaText: "¿Tienes saldo a favor? Te ayudamos a recuperarlo",
    ctaLink: "/registro"
  },

  // SAT Y TRÁMITES
  {
    slug: "sat",
    name: "SAT (Servicio de Administración Tributaria)",
    shortDefinition: "La autoridad fiscal de México. El organismo que cobra los impuestos.",
    category: "sat",
    categoryLabel: "Autoridad",
    priority: "alta",
    keywords: ["qué es el sat", "sat méxico", "servicio administración tributaria"],
    content: {
      whatIs: "El SAT es el organismo del gobierno federal encargado de recaudar los impuestos en México. Es la autoridad fiscal ante la cual todos los contribuyentes tienen obligaciones.",
      forWhat: "El SAT se encarga de:\n\n• Cobrar impuestos (ISR, IVA, IEPS, etc.)\n• Vigilar que los contribuyentes cumplan\n• Emitir reglas fiscales\n• Administrar aduanas",
      faqs: [
        { question: "¿Cómo contacto al SAT?", answer: "Por teléfono (55 6272 2728), chat en sat.gob.mx, o presencialmente con cita." },
        { question: "¿Qué trámites hago en el SAT?", answer: "RFC, e.firma, CSD, cambios de domicilio, consultas de adeudos, y más." }
      ]
    },
    relatedTerms: ["rfc", "efirma", "buzon-tributario"],
    ctaText: "Nos encargamos de tus trámites con el SAT",
    ctaLink: "/registro"
  },
  {
    slug: "buzon-tributario",
    name: "Buzón Tributario",
    shortDefinition: "Tu correo oficial con el SAT. Donde recibes notificaciones, requerimientos y avisos.",
    category: "sat",
    categoryLabel: "Trámite SAT",
    priority: "media",
    keywords: ["buzón tributario sat", "notificaciones sat"],
    content: {
      whatIs: "Es el sistema de comunicación oficial entre tú y el SAT. Por ahí te envían notificaciones, requerimientos, invitaciones a regularizarte, y más.",
      whoIsObliged: "Todos los contribuyentes activos deben tenerlo habilitado y revisarlo constantemente.",
      howItWorks: "Se activa desde el portal del SAT con tu e.firma. Una vez activo, tienes la obligación legal de revisarlo frecuentemente.",
      faqs: [
        { question: "¿Qué pasa si no lo reviso?", answer: "Las notificaciones se consideran legalmente recibidas aunque no las leas. Puedes tener problemas sin saberlo." },
        { question: "¿Me avisan al correo?", answer: "El SAT puede enviarte correos de aviso, pero la notificación oficial está en el buzón." }
      ]
    },
    relatedTerms: ["sat", "efirma"],
    ctaText: "¿No has revisado tu buzón? Te ayudamos",
    ctaLink: "/registro"
  },
  {
    slug: "opinion-de-cumplimiento",
    name: "Opinión de Cumplimiento (32-D)",
    shortDefinition: "El documento que dice si estás al corriente con tus obligaciones fiscales.",
    category: "sat",
    categoryLabel: "Trámite SAT",
    priority: "media",
    keywords: ["opinión de cumplimiento sat", "32-d", "opinión positiva"],
    content: {
      whatIs: "Es un documento oficial del SAT que indica si estás al corriente con tus obligaciones fiscales. Puede ser positiva (todo en orden) o negativa (tienes pendientes).",
      forWhat: "La necesitas para:\n\n• Participar en licitaciones públicas\n• Obtener subsidios o estímulos\n• Algunos trámites bancarios\n• Contratos con gobierno",
      faqs: [
        { question: "¿Cómo la obtengo?", answer: "En el portal del SAT con tu e.firma. Es gratuita e inmediata." },
        { question: "¿Qué hago si sale negativa?", answer: "Regularizar las obligaciones pendientes que te indique." }
      ]
    },
    relatedTerms: ["sat", "declaracion-mensual"],
    ctaText: "¿Tu opinión sale negativa? Te ayudamos a regularizarte",
    ctaLink: "/registro"
  },
  {
    slug: "constancia-situacion-fiscal",
    name: "Constancia de Situación Fiscal",
    shortDefinition: "El documento que muestra tu RFC, régimen fiscal, domicilio y obligaciones.",
    category: "sat",
    categoryLabel: "Documento SAT",
    priority: "media",
    keywords: ["constancia situación fiscal", "cómo sacar constancia sat"],
    content: {
      whatIs: "Es el documento oficial que resume tu información fiscal: RFC, nombre, domicilio, régimen fiscal y obligaciones. Es el 'acta de nacimiento' fiscal.",
      forWhat: "La necesitas para:\n\n• Que te facturen correctamente (CFDI 4.0)\n• Trámites bancarios\n• Contratos laborales\n• Verificar tu situación fiscal",
      howItWorks: "Se descarga gratis desde el portal del SAT o la app SAT ID.",
      faqs: [
        { question: "¿Necesito e.firma para obtenerla?", answer: "No necesariamente. Con contraseña SAT o SAT ID también puedes." },
        { question: "¿Tiene vigencia?", answer: "No tiene caducidad, pero te piden una reciente (menos de 3 meses generalmente)." }
      ]
    },
    relatedTerms: ["rfc", "regimen-fiscal", "sat"],
    ctaText: "¿Necesitas actualizar tu situación fiscal?",
    ctaLink: "/registro"
  },
  {
    slug: "cita-sat",
    name: "Cita SAT",
    shortDefinition: "La cita que debes agendar para realizar trámites presenciales en oficinas del SAT.",
    category: "sat",
    categoryLabel: "Trámite SAT",
    priority: "basica",
    keywords: ["cita sat", "agendar cita sat"],
    content: {
      whatIs: "Es la reservación que necesitas para acudir a una oficina del SAT a realizar trámites presenciales como e.firma, RFC, o aclaraciones.",
      howItWorks: "Se agenda en citasat.sat.gob.mx. Necesitas tu CURP o RFC, y elegir oficina, fecha y hora.",
      faqs: [
        { question: "¿Todos los trámites requieren cita?", answer: "No, muchos se pueden hacer en línea. Solo los que requieren presencia física." },
        { question: "¿Qué pasa si no hay citas?", answer: "Revisa constantemente, se liberan nuevas citas. También hay algunos SAT de menor demanda." }
      ]
    },
    relatedTerms: ["sat", "efirma", "rfc"],
    ctaText: "¿Te cuesta conseguir cita? Nosotros te asesoramos",
    ctaLink: "/contacto"
  },

  // TÉRMINOS CONTABLES BÁSICOS
  {
    slug: "ingresos",
    name: "Ingresos",
    shortDefinition: "Todo el dinero que recibes por tu trabajo, negocio o inversiones.",
    category: "contables",
    categoryLabel: "Término contable",
    priority: "basica",
    keywords: ["ingresos fiscales", "qué son ingresos"],
    content: {
      whatIs: "En términos fiscales, los ingresos son todo el dinero que recibes por tu actividad económica: ventas, servicios, honorarios, rentas, etc.",
      howItWorks: "Todos tus ingresos deben estar respaldados por facturas (CFDI) y se reportan en tus declaraciones mensuales y anual.",
      faqs: [
        { question: "¿Debo facturar todos mis ingresos?", answer: "Sí, todos los ingresos deben tener un CFDI que los respalde." },
        { question: "¿Qué ingresos están exentos?", answer: "Hay algunos exentos como herencias, donativos entre familiares, premios hasta cierto monto, etc." }
      ]
    },
    relatedTerms: ["egresos", "utilidad", "cfdi"],
    ctaText: "Te ayudamos a declarar tus ingresos",
    ctaLink: "/registro"
  },
  {
    slug: "egresos",
    name: "Egresos / Gastos",
    shortDefinition: "Todo el dinero que sale de tu bolsillo o negocio.",
    category: "contables",
    categoryLabel: "Término contable",
    priority: "basica",
    keywords: ["egresos fiscales", "gastos contables"],
    content: {
      whatIs: "Los egresos son todos los gastos que realizas en tu actividad económica: compras, servicios, nómina, renta, etc.",
      howItWorks: "Para que sean deducibles deben tener factura (CFDI) y ser estrictamente necesarios para tu actividad.",
      faqs: [
        { question: "¿Todos mis gastos son deducibles?", answer: "No, solo los relacionados con tu actividad y que cumplan requisitos fiscales." }
      ]
    },
    relatedTerms: ["ingresos", "utilidad", "deduccion-fiscal"],
    ctaText: "¿No sabes qué gastos deducir?",
    ctaLink: "/registro"
  },
  {
    slug: "utilidad",
    name: "Utilidad",
    shortDefinition: "Lo que te queda después de restar tus gastos de tus ingresos. Tu ganancia.",
    category: "contables",
    categoryLabel: "Término contable",
    priority: "basica",
    keywords: ["utilidad fiscal", "ganancia negocio"],
    content: {
      whatIs: "La utilidad es tu ganancia neta: lo que queda después de restar todos tus gastos de tus ingresos. Sobre esta base se calculan tus impuestos en la mayoría de los regímenes.",
      howItWorks: "Ingresos - Gastos = Utilidad\n\n$50,000 - $20,000 = $30,000 de utilidad",
      faqs: [
        { question: "¿Puedo tener utilidad negativa?", answer: "Sí, se llama pérdida fiscal y puedes amortizarla en años siguientes." }
      ]
    },
    relatedTerms: ["ingresos", "egresos", "base-gravable"],
    ctaText: "¿Quieres optimizar tu utilidad?",
    ctaLink: "/registro"
  },
  {
    slug: "base-gravable",
    name: "Base Gravable",
    shortDefinition: "El monto sobre el cual se calcula un impuesto.",
    category: "contables",
    categoryLabel: "Término contable",
    priority: "basica",
    keywords: ["base gravable", "base para impuestos"],
    content: {
      whatIs: "Es el monto sobre el que se aplica la tasa de impuesto. Por ejemplo, si la tasa de ISR es 30% y tu base gravable es $100,000, pagas $30,000.",
      howItWorks: "Generalmente: Base gravable = Ingresos - Deducciones permitidas",
      faqs: [
        { question: "¿Es lo mismo que utilidad?", answer: "En muchos casos sí, pero pueden haber ajustes adicionales." }
      ]
    },
    relatedTerms: ["utilidad", "isr", "deduccion-fiscal"],
    ctaText: "Te ayudamos a calcular tu base gravable",
    ctaLink: "/registro"
  },
  {
    slug: "ejercicio-fiscal",
    name: "Ejercicio Fiscal",
    shortDefinition: "El año calendario para efectos de impuestos: del 1 de enero al 31 de diciembre.",
    category: "contables",
    categoryLabel: "Término contable",
    priority: "basica",
    keywords: ["ejercicio fiscal", "año fiscal méxico"],
    content: {
      whatIs: "El ejercicio fiscal es el periodo de 12 meses sobre el cual se calculan tus impuestos anuales. En México coincide con el año calendario: del 1 de enero al 31 de diciembre.",
      faqs: [
        { question: "¿Puedo tener un ejercicio diferente?", answer: "Para personas físicas no. Algunas personas morales pueden tener ejercicios irregulares." }
      ]
    },
    relatedTerms: ["declaracion-anual", "isr"],
    ctaText: "Te ayudamos con tu ejercicio fiscal",
    ctaLink: "/registro"
  },
  {
    slug: "devengado-flujo-efectivo",
    name: "Devengado vs Flujo de Efectivo",
    shortDefinition: "Devengado: cuando facturas. Flujo de efectivo: cuando te pagan. Dos formas de medir ingresos.",
    category: "contables",
    categoryLabel: "Término contable",
    priority: "basica",
    keywords: ["devengado", "flujo de efectivo contable"],
    content: {
      whatIs: "Son dos métodos para reconocer ingresos y gastos:\n\n• Devengado: cuando emites la factura, aunque no te hayan pagado\n• Flujo de efectivo: cuando realmente recibes el dinero",
      howItWorks: "La mayoría de las personas físicas usan flujo de efectivo: pagas impuestos sobre lo que efectivamente cobraste.",
      faqs: [
        { question: "¿Cuál usa mi régimen?", answer: "RESICO y Actividad Empresarial PF generalmente usan flujo de efectivo." }
      ]
    },
    relatedTerms: ["ingresos", "cfdi"],
    ctaText: "¿Dudas sobre tu contabilidad?",
    ctaLink: "/registro"
  },
  {
    slug: "depreciacion",
    name: "Depreciación",
    shortDefinition: "Deducir gradualmente el costo de un activo (equipo, vehículo) a lo largo de su vida útil.",
    category: "contables",
    categoryLabel: "Término contable",
    priority: "basica",
    keywords: ["depreciación fiscal", "deducción de activos"],
    content: {
      whatIs: "Es el proceso de deducir fiscalmente el costo de un activo fijo (computadora, vehículo, maquinaria) a lo largo de varios años, en lugar de deducirlo todo de una vez.",
      howItWorks: "Cada tipo de activo tiene un porcentaje máximo de depreciación anual:\n\n• Computadoras: 30% anual\n• Vehículos: 25% anual\n• Maquinaria: 10% anual",
      faqs: [
        { question: "¿Por qué no deducir todo de una vez?", answer: "La ley establece tasas máximas para distribuir la deducción en el tiempo." }
      ]
    },
    relatedTerms: ["deduccion-fiscal", "gastos-deducibles"],
    ctaText: "Te ayudamos con tu depreciación",
    ctaLink: "/registro"
  },

  // SITUACIONES ESPECIALES
  {
    slug: "multas-recargos",
    name: "Multas y Recargos",
    shortDefinition: "Las penalizaciones que te cobra el SAT cuando no cumples tus obligaciones a tiempo.",
    category: "situaciones",
    categoryLabel: "Situación especial",
    priority: "basica",
    keywords: ["multas sat", "recargos impuestos"],
    content: {
      whatIs: "Son las sanciones económicas que el SAT aplica cuando no cumples con tus obligaciones fiscales a tiempo: no presentar declaraciones, no pagar impuestos, no expedir facturas, etc.",
      howItWorks: "• Multas: cantidad fija por cada obligación incumplida\n• Recargos: intereses sobre impuestos no pagados (aproximadamente 1.47% mensual)\n• Actualización: ajuste por inflación",
      faqs: [
        { question: "¿Puedo evitar multas?", answer: "Sí, cumpliendo a tiempo. Si ya tienes multas, hay programas de reducción." },
        { question: "¿Prescriben las multas?", answer: "Sí, después de 5 años en la mayoría de los casos." }
      ]
    },
    relatedTerms: ["sat", "declaracion-mensual"],
    ctaText: "¿Tienes multas? Te ayudamos a regularizarte",
    ctaLink: "/registro"
  },
  {
    slug: "cancelacion-facturas",
    name: "Cancelación de Facturas",
    shortDefinition: "El proceso de anular una factura que ya emitiste. Requiere aceptación del receptor.",
    category: "situaciones",
    categoryLabel: "Situación especial",
    priority: "basica",
    keywords: ["cancelar factura sat", "cancelación cfdi"],
    content: {
      whatIs: "Es el proceso de anular un CFDI que ya emitiste. Desde la versión 4.0, el receptor debe aceptar la cancelación en su buzón tributario.",
      howItWorks: "1. Solicitas la cancelación\n2. El receptor recibe notificación en su buzón\n3. Tiene 3 días para aceptar o rechazar\n4. Si no responde, se cancela automáticamente",
      faqs: [
        { question: "¿Puedo cancelar sin permiso del receptor?", answer: "Solo en ciertos casos: montos menores a $1,000 pesos o facturas del mismo día." },
        { question: "¿Qué pasa si el receptor rechaza?", answer: "La factura no se cancela. Deberás emitir una nota de crédito o llegar a un acuerdo." }
      ]
    },
    relatedTerms: ["cfdi", "nota-de-credito", "buzon-tributario"],
    ctaText: "¿Problemas con tus facturas?",
    ctaLink: "/registro"
  },
  {
    slug: "efos-edos",
    name: "EFOS y EDOS",
    shortDefinition: "EFOS: empresas que venden facturas falsas. EDOS: empresas que las compran. Ambas son ilegales.",
    category: "situaciones",
    categoryLabel: "Situación especial",
    priority: "media",
    keywords: ["efos sat", "edos sat", "facturas falsas"],
    content: {
      whatIs: "EFOS son Empresas que Facturan Operaciones Simuladas (venden facturas falsas). EDOS son Empresas que Deducen Operaciones Simuladas (compran esas facturas). Ambas prácticas son ilegales.",
      howItWorks: "El SAT publica listas de EFOS presuntos y definitivos. Si apareces o usas facturas de alguien en esas listas, tienes problemas serios.",
      faqs: [
        { question: "¿Cómo sé si un proveedor es EFOS?", answer: "Revisa las listas en el portal del SAT antes de hacer operaciones importantes." },
        { question: "¿Qué pasa si usé facturas de un EFOS?", answer: "Deberás aclarar ante el SAT, devolver las deducciones y posiblemente enfrentar sanciones." }
      ]
    },
    relatedTerms: ["cfdi", "sat", "deduccion-fiscal"],
    ctaText: "¿Te llegó un aviso de EFOS? Podemos ayudarte",
    ctaLink: "/contacto"
  },
  {
    slug: "suspension-actividades",
    name: "Suspensión de Actividades",
    shortDefinition: "El trámite para avisar al SAT que dejarás de tener actividad económica temporalmente.",
    category: "situaciones",
    categoryLabel: "Trámite SAT",
    priority: "basica",
    keywords: ["suspensión de actividades sat", "dejar de tributar"],
    content: {
      whatIs: "Es el aviso que presentas al SAT cuando dejas de realizar actividades económicas de forma temporal. Te exime de presentar declaraciones durante el periodo de suspensión.",
      howItWorks: "Se presenta en línea o presencialmente. Durante la suspensión no tienes obligaciones periódicas, pero debes reanudar cuando vuelvas a tener actividad.",
      faqs: [
        { question: "¿Puedo suspender si tengo adeudos?", answer: "Debes estar al corriente o al menos sin adeudos de periodos recientes." },
        { question: "¿Cuánto puede durar la suspensión?", answer: "Hasta 2 años. Después el SAT puede darte de baja definitiva." }
      ]
    },
    relatedTerms: ["sat", "regimen-fiscal"],
    ctaText: "¿Necesitas suspender actividades?",
    ctaLink: "/contacto"
  },
  {
    slug: "uma",
    name: "UMA (Unidad de Medida y Actualización)",
    shortDefinition: "La unidad de referencia para calcular multas, límites y obligaciones fiscales.",
    category: "situaciones",
    categoryLabel: "Referencia fiscal",
    priority: "basica",
    keywords: ["uma 2025", "unidad de medida actualización"],
    content: {
      whatIs: "La UMA es la referencia económica para calcular montos en leyes y obligaciones fiscales. Reemplazó al salario mínimo como unidad de referencia desde 2016.",
      howItWorks: "Para 2025:\n• UMA diaria: ~$113.14\n• UMA mensual: ~$3,439.46\n• UMA anual: ~$41,273.54",
      faqs: [
        { question: "¿Para qué se usa?", answer: "Multas, límites de deducciones, aportaciones, cuotas del IMSS, etc." },
        { question: "¿Es lo mismo que el salario mínimo?", answer: "No, la UMA es menor que el salario mínimo." }
      ]
    },
    relatedTerms: ["multas-recargos", "deducciones-personales"],
    ctaText: "¿Tienes dudas fiscales?",
    ctaLink: "/contacto"
  },
  {
    slug: "coeficiente-utilidad",
    name: "Coeficiente de Utilidad",
    shortDefinition: "El porcentaje que se usa para calcular tus pagos provisionales de ISR.",
    category: "situaciones",
    categoryLabel: "Cálculo fiscal",
    priority: "basica",
    keywords: ["coeficiente de utilidad", "cálculo pagos provisionales"],
    content: {
      whatIs: "Es un factor que se calcula dividiendo la utilidad fiscal entre los ingresos del ejercicio anterior. Se usa para estimar cuánto ISR deberás pagar mensualmente.",
      howItWorks: "Coeficiente = Utilidad fiscal / Ingresos nominales\n\nEjemplo: Si tu utilidad fue $500,000 y tus ingresos $2,000,000:\nCoeficiente = 0.25 (25%)",
      faqs: [
        { question: "¿Cuándo se aplica?", answer: "Para calcular los pagos provisionales de ISR mensuales." },
        { question: "¿Si es mi primer año?", answer: "No tienes coeficiente, pagas sobre utilidad real mensual." }
      ]
    },
    relatedTerms: ["pago-provisional", "isr", "utilidad"],
    ctaText: "Te calculamos tus pagos provisionales",
    ctaLink: "/registro"
  }
];

// Featured terms (most searched)
export const featuredTermSlugs = [
  "isr",
  "iva",
  "cfdi",
  "resico",
  "rfc",
  "sat",
  "declaracion-anual",
  "factura-electronica",
  "deduccion-fiscal",
  "persona-fisica"
];

// Get term by slug
export function getTermBySlug(slug: string): GlosarioTerm | undefined {
  return glosarioTerms.find(term => term.slug === slug);
}

// Get terms by category
export function getTermsByCategory(category: TermCategory): GlosarioTerm[] {
  return glosarioTerms.filter(term => term.category === category);
}

// Get related terms
export function getRelatedTerms(term: GlosarioTerm): GlosarioTerm[] {
  return term.relatedTerms
    .map(slug => getTermBySlug(slug))
    .filter((t): t is GlosarioTerm => t !== undefined)
    .slice(0, 6);
}

// Get terms grouped by first letter
export function getTermsByLetter(): Record<string, GlosarioTerm[]> {
  const grouped: Record<string, GlosarioTerm[]> = {};
  
  glosarioTerms.forEach(term => {
    // Get first letter, handling special cases
    let letter = term.name.charAt(0).toUpperCase();
    
    // Normalize accented characters
    const normalizeMap: Record<string, string> = {
      'Á': 'A', 'É': 'E', 'Í': 'I', 'Ó': 'O', 'Ú': 'U', 'Ñ': 'N'
    };
    letter = normalizeMap[letter] || letter;
    
    if (!grouped[letter]) {
      grouped[letter] = [];
    }
    grouped[letter].push(term);
  });
  
  // Sort terms within each letter
  Object.keys(grouped).forEach(letter => {
    grouped[letter].sort((a, b) => a.name.localeCompare(b.name, 'es'));
  });
  
  return grouped;
}

// Search terms
export function searchTerms(query: string): GlosarioTerm[] {
  const lowerQuery = query.toLowerCase().trim();
  if (!lowerQuery) return [];
  
  return glosarioTerms.filter(term => 
    term.name.toLowerCase().includes(lowerQuery) ||
    term.shortDefinition.toLowerCase().includes(lowerQuery) ||
    term.keywords.some(k => k.toLowerCase().includes(lowerQuery))
  );
}
